import React, { useEffect, useMemo, useState } from 'react';

// CorpusHealth — INTERNAL admin-only "Audee Intelligence Center" visibility page.
// Shows the current state and growth of Audee's intelligence system: an overall
// intelligence score, corpus completion/coverage, knowledge/consensus/
// recommendation totals, recommendation maturity, recent growth, recently added
// knowledge, a keyword Knowledge-Object lookup, health indicators, the SERVPRO
// Corpus v1 milestone, the next recommended corpus, the intelligence pipeline,
// and known bottlenecks.
//
// READ-ONLY dashboard. Fetches /api/admin/corpus-health/dashboard and renders it,
// plus an admin-only keyword lookup at /api/admin/corpus-health/search.
// Builds no rules, changes no audit behavior — pure internal visibility.

const API = 'https://tm-audit-platform-production.up.railway.app';

const num = (n) => (n == null ? '—' : Number(n).toLocaleString());
const pct = (n) => (n == null ? '—' : `${n}%`);
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString() : '—');

function scoreColor(score) {
    if (score == null) return 'var(--text-muted)';
    if (score >= 80) return 'var(--green)';
    if (score >= 60) return 'var(--blue)';
    if (score >= 40) return 'var(--amber)';
    return 'var(--red,#ef4444)';
}

// Plain-English maturity band for the overall score. Display label only — does
// NOT change the score formula.
function scoreBand(score) {
    if (score == null) return '—';
    if (score >= 90) return 'Exceptional';
    if (score >= 80) return 'Enterprise Ready';
    if (score >= 60) return 'Strong';
    if (score >= 40) return 'Growing';
    return 'Developing';
}

function KpiCard({ label, value, accent, sub }) {
    return (
        <div className="gd-kpi">
            <div className="gd-kpi-label">{label}</div>
            <div className="gd-kpi-value" style={accent ? { color: accent } : undefined}>{value}</div>
            {sub && <div className="gd-kpi-sub">{sub}</div>}
        </div>
    );
}

const STATUS_META = {
    complete:    { label: 'Complete',    color: 'var(--green)' },
    in_progress: { label: 'In Progress', color: 'var(--blue)' },
    not_started: { label: 'Not Started', color: 'var(--text-muted)' },
};

function CompletionCard({ card }) {
    const meta = STATUS_META[card.status] || STATUS_META.not_started;
    const totalLabel = card.total == null
        ? (card.done ? num(card.done) : '0')
        : `${num(card.done)} / ${num(card.total)}`;
    const subLabel = card.total == null && !card.done ? '0 / Unknown' : null;
    const pctDone = card.total ? Math.min(100, Math.round((card.done / card.total) * 100)) : (card.done ? 100 : 0);
    return (
        <div className="gd-kpi" style={{ borderTop: `3px solid ${meta.color}` }}>
            <div className="gd-kpi-label">{card.label}</div>
            <div className="gd-kpi-value" style={{ color: meta.color }}>{totalLabel}</div>
            <div className="gd-kpi-sub" style={{ color: meta.color, fontWeight: 700 }}>{subLabel || meta.label}</div>
            {card.total != null && (
                <div style={{ height: 5, background: 'var(--border,#2a2a2a)', borderRadius: 3, marginTop: 8, overflow: 'hidden' }}>
                    <div style={{ width: `${pctDone}%`, height: '100%', background: meta.color }} />
                </div>
            )}
        </div>
    );
}

// One horizontal coverage row. Known target → real %; unknown target → count +
// status chip (never a fake percentage).
function CoverageBar({ card }) {
    const meta = STATUS_META[card.status] || STATUS_META.not_started;
    const known = card.total != null;
    const pctDone = known ? Math.min(100, Math.round((card.done / card.total) * 100)) : 0;
    return (
        <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{card.label}</span>
                <span style={{ fontSize: 12, color: meta.color, fontWeight: 700 }}>
                    {known
                        ? `${num(card.done)} / ${num(card.total)} · ${pctDone}%`
                        : `${num(card.done || 0)} · target unknown · ${meta.label}`}
                </span>
            </div>
            <div style={{ height: 8, background: 'var(--border,#2a2a2a)', borderRadius: 4, overflow: 'hidden' }}>
                {known
                    ? <div style={{ width: `${pctDone}%`, height: '100%', background: meta.color }} />
                    // Unknown target: striped indeterminate fill sized to "some progress
                    // exists" vs "not started" — explicitly NOT a measured percentage.
                    : <div style={{ width: card.done ? '40%' : '0%', height: '100%', background: meta.color, opacity: 0.5 }} />}
            </div>
        </div>
    );
}

// Recommendation maturity — proportional segmented bar making it visually obvious
// that recommendations are overwhelmingly Experimental and internal-only.
function MaturityBar({ ready, experimental, conflicted }) {
    const r = ready || 0, e = experimental || 0, c = conflicted || 0;
    const total = r + e + c;
    const seg = (v, color) => (total ? { width: `${(v / total) * 100}%`, background: color } : null);
    const rows = [
        { label: 'Ready', value: r, color: 'var(--green)' },
        { label: 'Experimental', value: e, color: 'var(--amber)' },
        { label: 'Conflicted', value: c, color: 'var(--red,#ef4444)' },
    ];
    return (
        <div className="gd-kpi" style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', height: 22, borderRadius: 6, overflow: 'hidden', background: 'var(--border,#2a2a2a)', marginBottom: 12 }}>
                {total === 0
                    ? <div style={{ alignSelf: 'center', paddingLeft: 10, fontSize: 12, color: 'var(--text-muted)' }}>No recommendations yet</div>
                    : rows.filter(x => x.value > 0).map(x => (
                        <div key={x.label} style={{ ...seg(x.value, x.color), height: '100%' }} title={`${x.label}: ${x.value}`} />
                    ))}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px' }}>
                {rows.map(x => (
                    <span key={x.label} style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: x.color, marginRight: 6 }} />
                        {x.label}: <b style={{ color: 'var(--text)' }}>{num(x.value)}</b>
                        {total ? <span style={{ color: 'var(--text-muted)' }}> ({Math.round((x.value / total) * 100)}%)</span> : null}
                    </span>
                ))}
            </div>
            <div style={{ fontSize: 12, color: 'var(--amber)', marginTop: 10, fontWeight: 600 }}>
                Most recommendations are still Experimental — internal-only and not customer-facing.
            </div>
        </div>
    );
}

const TIER_COLOR = { high: 'var(--green)', medium: 'var(--blue)', low: 'var(--text-muted)' };

function RecentItem({ ko }) {
    const tier = (ko.quality_tier || '').toLowerCase();
    const impacts = Array.isArray(ko.business_impact) ? ko.business_impact : [];
    const acct = ko.account || ko.carrier || ko.issuer;
    return (
        <div className="gd-kpi" style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{ko.title || '(untitled fact)'}</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDateTime(ko.created_at)}</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                {ko.object_type && <span style={{ color: 'var(--blue)', fontWeight: 600 }}>{ko.object_type}</span>}
                {acct && <span>Account: <b style={{ color: 'var(--text)' }}>{acct}</b></span>}
                {tier && <span>Quality: <b style={{ color: TIER_COLOR[tier] || 'var(--text)' }}>{tier}{ko.quality_score != null ? ` (${ko.quality_score})` : ''}</b></span>}
                {impacts.length > 0 && <span>Impact: <b style={{ color: 'var(--text)' }}>{impacts.join(', ')}</b></span>}
                {ko.source_filename && <span>Source: <b style={{ color: 'var(--text)' }}>{ko.source_filename}</b></span>}
            </div>
        </div>
    );
}

const PIPELINE = [
    'Corpus Documents',
    'Knowledge Extraction',
    'Knowledge Quality',
    'Knowledge Consensus',
    'Internal Recommendations',
    'Future Audit Intelligence',
];

const BOTTLENECKS = [
    'Program Requirement is still a broad catch-all and should be reclassified later.',
    'Recommendations are mostly Experimental and should stay internal for now.',
    'Full-corpus reprocessing should eventually become incremental rather than full reruns.',
    'Carrier and outcome data are needed before recommendations can become customer-facing.',
];

// ── Overall Intelligence Score header (explainable: components shown as subtext) ──
function ScoreHeader({ score }) {
    if (!score) return null;
    const color = scoreColor(score.overall);
    return (
        <div className="gd-kpi" style={{ borderLeft: `4px solid ${color}`, marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap' }}>
                <div>
                    <div className="gd-kpi-label">Audee Intelligence</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color, margin: '2px 0' }}>{scoreBand(score.overall)}</div>
                    <div style={{ fontSize: 40, fontWeight: 800, color, lineHeight: 1.1 }}>
                        {score.overall}<span style={{ fontSize: 20, color: 'var(--text-muted)', fontWeight: 600 }}> / 100</span>
                    </div>
                </div>
                <div style={{ flex: 1, minWidth: 280 }}>
                    {(score.components || []).map(c => (
                        <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span style={{ fontSize: 12, color: 'var(--text-muted)', width: 200 }}>{c.label}</span>
                            <div style={{ flex: 1, height: 6, background: 'var(--border,#2a2a2a)', borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ width: `${c.score}%`, height: '100%', background: scoreColor(c.score) }} />
                            </div>
                            <span style={{ fontSize: 12, color: 'var(--text)', width: 78, textAlign: 'right' }}>
                                {c.score} · w{Math.round(c.weight * 100)}%
                            </span>
                        </div>
                    ))}
                </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
                Transparent weighted average of measurable internal metrics — no AI scoring.
            </div>
        </div>
    );
}

// ── Search Intelligence — keyword KO lookup (not chat, not an AI answer) ──
function KnowledgeSearch({ headers }) {
    const [q, setQ] = useState('');
    const [results, setResults] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const run = async (e) => {
        if (e) e.preventDefault();
        const query = q.trim();
        if (!query) { setResults(null); return; }
        setBusy(true); setErr(null);
        try {
            const res = await fetch(`${API}/api/admin/corpus-health/search?q=${encodeURIComponent(query)}`, { headers });
            if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
            const data = await res.json();
            setResults(data.results || []);
        } catch (e2) { setErr(e2.message); setResults(null); }
        setBusy(false);
    };

    return (
        <div className="gd-kpi" style={{ marginBottom: 18 }}>
            <form onSubmit={run} style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <input
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    placeholder="Keyword search across current Knowledge Objects…"
                    style={{ flex: 1, padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border,#2a2a2a)', background: 'var(--card-bg,#1a1a1a)', color: 'var(--text)', fontSize: 13 }}
                />
                <button className="history-back-link" type="submit" disabled={busy}>{busy ? 'Searching…' : 'Search'}</button>
            </form>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
                Keyword lookup across current Knowledge Objects — not chat, not an AI answer.
            </div>
            {err && <div className="gd-error">Search failed: {err}</div>}
            {results && results.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No matching Knowledge Objects.</div>}
            {results && results.length > 0 && (
                <div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>{results.length} match{results.length === 1 ? '' : 'es'}</div>
                    {results.map(r => {
                        const tier = (r.quality_tier || '').toLowerCase();
                        const acct = r.account || r.carrier || r.issuer;
                        return (
                            <div key={r.id} className="gd-kpi" style={{ marginBottom: 8 }}>
                                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{r.title || '(untitled fact)'}</div>
                                {r.description && <div style={{ fontSize: 13, color: 'var(--text-muted)', margin: '4px 0', lineHeight: 1.5 }}>{r.description}</div>}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', fontSize: 12, color: 'var(--text-muted)' }}>
                                    {r.object_type && <span style={{ color: 'var(--blue)', fontWeight: 600 }}>{r.object_type}</span>}
                                    {acct && <span>Account: <b style={{ color: 'var(--text)' }}>{acct}</b></span>}
                                    {r.carrier && <span>Carrier: <b style={{ color: 'var(--text)' }}>{r.carrier}</b></span>}
                                    {r.issuer && <span>Issuer: <b style={{ color: 'var(--text)' }}>{r.issuer}</b></span>}
                                    {tier && <span>Quality: <b style={{ color: TIER_COLOR[tier] || 'var(--text)' }}>{tier}{r.quality_score != null ? ` (${r.quality_score})` : ''}</b></span>}
                                    {r.source_filename && <span>Source: <b style={{ color: 'var(--text)' }}>{r.source_filename}</b></span>}
                                    {!r.source_filename && r.carrier_document_id && <span>Source doc: <b style={{ color: 'var(--text)' }}>{r.carrier_document_id}</b></span>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function CorpusHealth({ session }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const headers = useMemo(() => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`,
    }), [session]);

    const load = async () => {
        setLoading(true); setErr(null);
        try {
            const res = await fetch(API + '/api/admin/corpus-health/dashboard', { headers });
            if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
            setData(await res.json());
        } catch (e) { setErr(e.message); }
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

    if (loading) return <div className="history-loading"><div className="spinner" /></div>;
    if (err) return <div className="gd-error">Failed to load Audee Intelligence: {err} <button className="history-back-link" onClick={load}>Retry</button></div>;
    if (!data) return null;

    const t = data.totals || {};
    const g = data.growth || {};
    const h = data.health || {};
    const v1 = data.servproV1 || {};
    const recent = data.recent || [];

    return (
        <div className="gd-root">
            <div className="gd-toolbar">
                <div className="gd-generated">Audee Intelligence Center · generated {fmtDateTime(data.generatedAt)} · internal visibility only</div>
                <button className="history-back-link" onClick={load}>↻ Refresh</button>
            </div>

            {/* ── Overall Intelligence Score ── */}
            <ScoreHeader score={data.intelligenceScore} />

            {/* Internal-only disclaimer ── */}
            <div style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--card-bg,#1a1a1a)', border: '1px solid var(--border,#2a2a2a)', borderRadius: 8, padding: '8px 12px', marginBottom: 18 }}>
                Internal intelligence visibility only. Recommendations are not customer-facing and do not affect audit results.
            </div>

            {/* ── Corpus completion cards ── */}
            <div className="gd-section-title">Corpus Completion</div>
            <div className="gd-kpi-grid">
                {(data.completion || []).map(card => <CompletionCard key={card.key} card={card} />)}
            </div>

            {/* ── Corpus Coverage progress bars ── */}
            <div className="gd-section-title">Corpus Coverage</div>
            <div className="gd-kpi" style={{ marginBottom: 18 }}>
                {(data.completion || []).map(card => <CoverageBar key={card.key} card={card} />)}
            </div>

            {/* ── SERVPRO Corpus v1 status ── */}
            <div className="gd-section-title">SERVPRO Corpus v1 Status</div>
            <div className="gd-kpi" style={{ borderLeft: '4px solid var(--green)', marginBottom: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <span style={{ color: 'var(--green)', fontWeight: 800, fontSize: 16 }}>✓ SERVPRO Corpus v1 Complete</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 24px', fontSize: 13, color: 'var(--text-muted)' }}>
                    <span><b style={{ color: 'var(--text)' }}>{num(v1.uniqueBulletins)}</b> unique SERVPRO bulletins</span>
                    <span><b style={{ color: 'var(--green)' }}>{num(v1.extractionFailures)}</b> extraction failures</span>
                    <span><b style={{ color: 'var(--green)' }}>{num(v1.unknownDocuments)}</b> unknown documents</span>
                    <span><b style={{ color: 'var(--text)' }}>{num(v1.duplicatePdfs)}</b> duplicate PDFs</span>
                    <span><b style={{ color: 'var(--text)' }}>{num(v1.nonServproFiltered)}</b> non-SERVPRO files filtered</span>
                    <span>Completed <b style={{ color: 'var(--text)' }}>{v1.lastCompletedAt || '—'}</b></span>
                </div>
            </div>

            {/* ── Current intelligence totals ── */}
            <div className="gd-section-title">Current Intelligence Totals</div>
            <div className="gd-kpi-grid">
                <KpiCard label="Carrier Documents" value={num(t.carrierDocsTotal)} accent="var(--amber)" />
                <KpiCard label="SERVPRO Bulletins" value={num(t.servproBulletins)} />
                <KpiCard label="Knowledge Objects (all)" value={num(t.koTotal)} />
                <KpiCard label="Current Knowledge Objects" value={num(t.koCurrent)} accent="var(--blue)" />
                <KpiCard label="High-Signal Objects" value={num(t.koHighSignal)} accent="var(--green)" />
                <KpiCard label="Hidden Low-Signal Objects" value={num(t.koHiddenLow)} accent="var(--text-muted)" />
                <KpiCard label="Program Requirement" value={num(t.koProgReq)} />
                <KpiCard label="Moisture Documentation" value={num(t.koMoisture)} />
                <KpiCard label="Consensus Groups" value={num(t.cgTotal)} />
                <KpiCard label="Ready Consensus Groups" value={num(t.cgReady)} accent="var(--green)" />
                <KpiCard label="Conflicted Consensus Groups" value={num(t.cgConflicted)} accent="var(--red,#ef4444)" />
                <KpiCard label="Recommendations" value={num(t.recTotal)} />
                <KpiCard label="Ready Recommendations" value={num(t.recReady)} accent="var(--green)" />
                <KpiCard label="Experimental Recommendations" value={num(t.recExperimental)} accent="var(--amber)" />
                <KpiCard label="Conflicted Recommendations" value={num(t.recConflicted)} accent="var(--red,#ef4444)" />
            </div>

            {/* ── Recommendation Maturity ── */}
            <div className="gd-section-title">Recommendation Maturity</div>
            <MaturityBar ready={t.recReady} experimental={t.recExperimental} conflicted={t.recConflicted} />

            {/* ── Recent Intelligence Added ── */}
            <div className="gd-section-title">Recent Intelligence Added</div>
            {recent.length === 0
                ? <div className="gd-kpi" style={{ marginBottom: 18, fontSize: 13, color: 'var(--text-muted)' }}>No recent Knowledge Objects.</div>
                : <div style={{ marginBottom: 18 }}>{recent.map(ko => <RecentItem key={ko.id} ko={ko} />)}</div>}

            {/* ── Search Intelligence ── */}
            <div className="gd-section-title">Search Intelligence</div>
            <KnowledgeSearch headers={headers} />

            {/* ── Last 24 hours / recent growth ── */}
            <div className="gd-section-title">Recent Growth · Last 24 Hours</div>
            <div className="gd-kpi-grid">
                <KpiCard label="Documents Added" value={num(g.documentsAdded)} accent="var(--amber)" />
                <KpiCard label="Knowledge Objects Created" value={num(g.koCreated)} accent="var(--blue)" />
                <KpiCard label="High-Signal Objects Added" value={num(g.highSignalAdded)} accent="var(--green)" />
                <KpiCard label="Recommendations Added" value={num(g.recsAdded)} />
                <KpiCard label="Ready Recommendations Added" value={num(g.readyRecsAdded)} accent="var(--green)" />
                <KpiCard label="Conflicts Added / Resolved" value={num(g.conflictsAdded)} accent="var(--red,#ef4444)" />
            </div>

            {/* ── Corpus health indicators ── */}
            <div className="gd-section-title">Corpus Health Indicators</div>
            <div className="gd-kpi-grid">
                <KpiCard label="Extraction Failure Rate" value={pct(h.extractionFailureRate)} accent={(h.extractionFailureRate || 0) > 0 ? 'var(--red,#ef4444)' : 'var(--green)'} sub={h.extractionFailures != null ? `${num(h.extractionFailures)} docs` : null} />
                <KpiCard label="Unknown Documents" value={num(h.unknownDocCount)} accent={(h.unknownDocCount || 0) > 0 ? 'var(--amber)' : 'var(--green)'} />
                <KpiCard label="Duplicate Count" value={num(h.duplicateCount)} sub="known v1 dedup" />
                <KpiCard label="Classifier Health" value={pct(h.classifierHealth)} accent="var(--green)" sub="attributed share" />
                <KpiCard label="Program Requirement Ratio" value={pct(h.programRequirementRatio)} accent="var(--amber)" sub="of current objects" />
                <KpiCard label="Experimental Rec Ratio" value={pct(h.experimentalRecRatio)} accent="var(--amber)" sub="of recommendations" />
            </div>

            {/* ── Next recommended corpus ── */}
            <div className="gd-section-title">Next Recommended Corpus</div>
            <div className="gd-kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
                <div className="gd-kpi" style={{ borderLeft: '4px solid var(--green)' }}>
                    <div className="gd-kpi-label" style={{ color: 'var(--green)' }}>Primary</div>
                    <div style={{ fontWeight: 800, fontSize: 15, margin: '4px 0 6px' }}>Carrier / Insurer Program Documents</div>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                        Cross-issuer consensus will let Audee compare SERVPRO program requirements against carrier-authored requirements.
                    </div>
                </div>
                <div className="gd-kpi" style={{ borderLeft: '4px solid var(--blue)' }}>
                    <div className="gd-kpi-label" style={{ color: 'var(--blue)' }}>Secondary</div>
                    <div style={{ fontWeight: 800, fontSize: 15, margin: '4px 0 6px' }}>Historical Estimates / Supplement Outcomes</div>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                        This teaches Audee what actually gets paid, not just what documents say.
                    </div>
                </div>
            </div>

            {/* ── Intelligence pipeline ── */}
            <div className="gd-section-title">Intelligence Pipeline</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 18 }}>
                {PIPELINE.map((step, i) => (
                    <React.Fragment key={step}>
                        <div style={{
                            padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                            background: i === PIPELINE.length - 1 ? 'transparent' : 'var(--card-bg,#1a1a1a)',
                            border: `1px solid ${i === PIPELINE.length - 1 ? 'var(--border,#2a2a2a)' : 'var(--blue)'}`,
                            color: i === PIPELINE.length - 1 ? 'var(--text-muted)' : 'var(--text)',
                        }}>
                            {step}
                        </div>
                        {i < PIPELINE.length - 1 && <span style={{ color: 'var(--text-muted)', fontWeight: 700 }}>→</span>}
                    </React.Fragment>
                ))}
            </div>

            {/* ── Known bottlenecks ── */}
            <div className="gd-section-title">Known Bottlenecks</div>
            <div className="gd-kpi" style={{ marginBottom: 18 }}>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7 }}>
                    {BOTTLENECKS.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
            </div>
        </div>
    );
}

export default CorpusHealth;
